import { IconData } from './types';

/**
 * Curated list of popular tech icons for the sidebar
 * 
 * Icon format: "source:name"
 * - si: SimpleIcons (brands, services)
 * - mdi: Material Design Icons (infrastructure, general)
 * - carbon: IBM Carbon Icons (cloud, enterprise)
 * - lucide: Lucide Icons (UI)
 * - tabler: Tabler Icons (UI)
 * - homelab: Dashboard Icons (1800+ homelab/self-hosted service icons)
 * 
 * For backward compatibility, SimpleIcons can also use just the slug name
 */
export const POPULAR_ICONS: IconData[] = [
  // === Frontend Frameworks ===
  { title: 'React', icon: 'si:react', hex: '61DAFB', category: 'frontend' },
  { title: 'Vue.js', icon: 'si:vuedotjs', hex: '4FC08D', category: 'frontend' },
  { title: 'Angular', icon: 'si:angular', hex: 'DD0031', category: 'frontend' },
  { title: 'Svelte', icon: 'si:svelte', hex: 'FF3E00', category: 'frontend' },
  { title: 'Next.js', icon: 'si:nextdotjs', hex: '000000', category: 'frontend' },
  { title: 'Nuxt', icon: 'si:nuxt', hex: '00DC82', category: 'frontend' },
  { title: 'Astro', icon: 'si:astro', hex: 'FF5D01', category: 'frontend' },

  // === Languages ===
  { title: 'TypeScript', icon: 'si:typescript', hex: '3178C6', category: 'frontend' },
  { title: 'JavaScript', icon: 'si:javascript', hex: 'F7DF1E', category: 'frontend' },
  { title: 'Python', icon: 'si:python', hex: '3776AB', category: 'backend' },
  { title: 'Go', icon: 'si:go', hex: '00ADD8', category: 'backend' },
  { title: 'Rust', icon: 'si:rust', hex: '000000', category: 'backend' },
  { title: 'Java', icon: 'si:openjdk', hex: '000000', category: 'backend' },
  { title: 'C#', icon: 'logos:c-sharp', hex: '512BD4', category: 'backend' },
  { title: 'PHP', icon: 'si:php', hex: '777BB4', category: 'backend' },

  // === Backend Frameworks ===
  { title: 'Node.js', icon: 'si:nodedotjs', hex: '339933', category: 'backend' },
  { title: 'Django', icon: 'si:django', hex: '092E20', category: 'backend' },
  { title: 'Flask', icon: 'si:flask', hex: '000000', category: 'backend' },
  { title: 'FastAPI', icon: 'si:fastapi', hex: '009688', category: 'backend' },
  { title: 'Spring', icon: 'si:spring', hex: '6DB33F', category: 'backend' },
  { title: 'Express', icon: 'si:express', hex: '000000', category: 'backend' },
  { title: '.NET', icon: 'si:dotnet', hex: '512BD4', category: 'backend' },
  { title: 'Laravel', icon: 'si:laravel', hex: 'FF2D20', category: 'backend' },

  // === Databases ===
  { title: 'PostgreSQL', icon: 'si:postgresql', hex: '4169E1', category: 'database' },
  { title: 'MySQL', icon: 'si:mysql', hex: '4479A1', category: 'database' },
  { title: 'MongoDB', icon: 'si:mongodb', hex: '47A248', category: 'database' },
  { title: 'Redis', icon: 'si:redis', hex: 'DC382D', category: 'database' },
  { title: 'SQLite', icon: 'si:sqlite', hex: '003B57', category: 'database' },
  { title: 'Cassandra', icon: 'si:apachecassandra', hex: '1287B1', category: 'database' },
  { title: 'Elasticsearch', icon: 'si:elasticsearch', hex: '005571', category: 'database' },
  { title: 'InfluxDB', icon: 'si:influxdb', hex: '22ADF6', category: 'database' },

  // === Cloud Platforms ===
  { title: 'AWS', icon: 'logos:aws', hex: '232F3E', category: 'cloud' },
  { title: 'Google Cloud', icon: 'si:googlecloud', hex: '4285F4', category: 'cloud' },
  { title: 'Azure', icon: 'logos:microsoft-azure', hex: '0078D4', category: 'cloud' },
  { title: 'DigitalOcean', icon: 'si:digitalocean', hex: '0080FF', category: 'cloud' },
  { title: 'Cloudflare', icon: 'si:cloudflare', hex: 'F38020', category: 'cloud' },
  { title: 'Vercel', icon: 'si:vercel', hex: '000000', category: 'cloud' },
  { title: 'Netlify', icon: 'si:netlify', hex: '00C7B7', category: 'cloud' },
  { title: 'Heroku', icon: 'logos:heroku-icon', hex: '430098', category: 'cloud' },
  { title: 'Hostinger', icon: 'si:hostinger', hex: '673DE6', category: 'cloud' },

  // === DevOps & Containers ===
  { title: 'Docker', icon: 'si:docker', hex: '2496ED', category: 'devops' },
  { title: 'Kubernetes', icon: 'si:kubernetes', hex: '326CE5', category: 'devops' },
  { title: 'Terraform', icon: 'si:terraform', hex: '7B42BC', category: 'devops' },
  { title: 'Ansible', icon: 'si:ansible', hex: 'EE0000', category: 'devops' },
  { title: 'Jenkins', icon: 'si:jenkins', hex: 'D24939', category: 'devops' },
  { title: 'GitHub Actions', icon: 'si:githubactions', hex: '2088FF', category: 'devops' },
  { title: 'GitLab CI', icon: 'si:gitlab', hex: 'FC6D26', category: 'devops' },
  { title: 'ArgoCD', icon: 'si:argo', hex: 'EF7B4D', category: 'devops' },

  // === BaaS & Services ===
  { title: 'Supabase', icon: 'si:supabase', hex: '3ECF8E', category: 'cloud' },
  { title: 'Firebase', icon: 'si:firebase', hex: 'FFCA28', category: 'cloud' },
  { title: 'Auth0', icon: 'si:auth0', hex: 'EB5424', category: 'security' },
  { title: 'Stripe', icon: 'si:stripe', hex: '008CDD', category: 'brands' },
  { title: 'Twilio', icon: 'logos:twilio', hex: 'F22F46', category: 'brands' },

  // === Monitoring & Observability ===
  { title: 'Grafana', icon: 'si:grafana', hex: 'F46800', category: 'monitoring' },
  { title: 'Prometheus', icon: 'si:prometheus', hex: 'E6522C', category: 'monitoring' },
  { title: 'Datadog', icon: 'si:datadog', hex: '632CA6', category: 'monitoring' },
  { title: 'New Relic', icon: 'si:newrelic', hex: '008C99', category: 'monitoring' },
  { title: 'Sentry', icon: 'si:sentry', hex: '362D59', category: 'monitoring' },

  // === Message Queues ===
  { title: 'Kafka', icon: 'si:apachekafka', hex: '231F20', category: 'infrastructure' },
  { title: 'RabbitMQ', icon: 'si:rabbitmq', hex: 'FF6600', category: 'infrastructure' },
  { title: 'EventStore', icon: 'si:eventstore', hex: '5AB552', category: 'infrastructure' },

  // === Web Servers & Proxies ===
  { title: 'Nginx', icon: 'si:nginx', hex: '009639', category: 'infrastructure' },
  { title: 'Apache', icon: 'si:apache', hex: 'D22128', category: 'infrastructure' },
  { title: 'Traefik', icon: 'si:traefikproxy', hex: '24A1C1', category: 'infrastructure' },
  { title: 'HAProxy', icon: 'mdi:server-network', hex: '106DA9', category: 'infrastructure' },

  // === Version Control ===
  { title: 'Git', icon: 'si:git', hex: 'F05032', category: 'devops' },
  { title: 'GitHub', icon: 'si:github', hex: '181717', category: 'devops' },
  { title: 'GitLab', icon: 'si:gitlab', hex: 'FC6D26', category: 'devops' },
  { title: 'Bitbucket', icon: 'si:bitbucket', hex: '0052CC', category: 'devops' },

  // === Styling ===
  { title: 'Tailwind CSS', icon: 'si:tailwindcss', hex: '06B6D4', category: 'frontend' },
  { title: 'Sass', icon: 'si:sass', hex: 'CC6699', category: 'frontend' },
  { title: 'CSS3', icon: 'si:css', hex: '1572B6', category: 'frontend' },

  // === API & Data ===
  { title: 'GraphQL', icon: 'si:graphql', hex: 'E10098', category: 'backend' },
  { title: 'Apollo', icon: 'si:apollographql', hex: '311C87', category: 'backend' },
  { title: 'Prisma', icon: 'si:prisma', hex: '2D3748', category: 'backend' },
  { title: 'tRPC', icon: 'si:trpc', hex: '2596BE', category: 'backend' },
  { title: 'API Gateway', icon: 'logos:aws-api-gateway', hex: 'FF4F8B', category: 'backend' },

  // === AI & ML ===
  { title: 'OpenAI', icon: 'homelab:openai', hex: '412991', category: 'brands' },
  { title: 'TensorFlow', icon: 'si:tensorflow', hex: 'FF6F00', category: 'backend' },
  { title: 'PyTorch', icon: 'si:pytorch', hex: 'EE4C2C', category: 'backend' },
  { title: 'Hugging Face', icon: 'si:huggingface', hex: 'FFD21E', category: 'brands' },
  { title: 'Apache Airflow', icon: 'si:apacheairflow', hex: '017CEE', category: 'devops' },
  { title: 'Apache Spark', icon: 'si:apachespark', hex: 'E25A1C', category: 'backend' },
  { title: 'Kubeflow', icon: 'carbon:machine-learning-model', hex: '526274', category: 'devops' },

  // === OS & Platforms ===
  { title: 'Linux', icon: 'si:linux', hex: 'FCC624', category: 'infrastructure' },
  { title: 'Ubuntu', icon: 'si:ubuntu', hex: 'E95420', category: 'infrastructure' },
  { title: 'Debian', icon: 'si:debian', hex: 'A81D33', category: 'infrastructure' },
  { title: 'Windows', icon: 'logos:microsoft-windows-icon', hex: '0078D6', category: 'infrastructure' },

  // === Design Tools ===
  { title: 'Figma', icon: 'si:figma', hex: 'F24E1E', category: 'brands' },

  // === Homelab / Self-hosted (using MDI for infrastructure icons) ===
  { title: 'Server', icon: 'mdi:server', hex: '64748b', category: 'infrastructure' },
  { title: 'Server Network', icon: 'mdi:server-network', hex: '64748b', category: 'infrastructure' },
  { title: 'NAS', icon: 'mdi:nas', hex: '64748b', category: 'storage' },
  { title: 'Router', icon: 'mdi:router-network', hex: '3b82f6', category: 'network' },
  { title: 'Firewall', icon: 'mdi:wall-fire', hex: 'ef4444', category: 'security' },
  { title: 'Switch', icon: 'mdi:switch', hex: '64748b', category: 'network' },
  { title: 'Access Point', icon: 'mdi:access-point-network', hex: '22c55e', category: 'network' },
  { title: 'Cloud Server', icon: 'mdi:cloud-outline', hex: '3b82f6', category: 'cloud' },
  { title: 'Database Server', icon: 'mdi:database', hex: '8b5cf6', category: 'database' },
  { title: 'Load Balancer', icon: 'mdi:scale-balance', hex: 'f59e0b', category: 'infrastructure' },
  { title: 'DNS', icon: 'mdi:dns', hex: '06b6d4', category: 'network' },
  { title: 'VPN', icon: 'mdi:vpn', hex: '22c55e', category: 'security' },
  { title: 'Backup', icon: 'mdi:backup-restore', hex: '64748b', category: 'storage' },
  { title: 'Storage', icon: 'mdi:harddisk', hex: '64748b', category: 'storage' },
  { title: 'Cluster', icon: 'mdi:server-network-outline', hex: '8b5cf6', category: 'infrastructure' },
  { title: 'Container', icon: 'mdi:docker', hex: '2496ED', category: 'devops' },
  { title: 'Virtual Machine', icon: 'mdi:monitor-screenshot', hex: '3b82f6', category: 'infrastructure' },
  { title: 'API Gateway', icon: 'mdi:api', hex: 'f59e0b', category: 'infrastructure' },
  { title: 'CDN', icon: 'mdi:web', hex: 'f97316', category: 'infrastructure' },
  { title: 'Cache', icon: 'mdi:cached', hex: 'ef4444', category: 'infrastructure' },
  { title: 'Queue', icon: 'mdi:tray-full', hex: 'f59e0b', category: 'infrastructure' },
  { title: 'Microservice', icon: 'mdi:cube-outline', hex: '8b5cf6', category: 'infrastructure' },
  { title: 'Lambda', icon: 'mdi:lambda', hex: 'f59e0b', category: 'cloud' },
  { title: 'Cron Job', icon: 'mdi:clock-outline', hex: '64748b', category: 'infrastructure' },
  { title: 'Webhook', icon: 'mdi:webhook', hex: '3b82f6', category: 'infrastructure' },
  { title: 'Email Server', icon: 'mdi:email-outline', hex: '64748b', category: 'infrastructure' },
  { title: 'S3 Bucket', icon: 'mdi:bucket-outline', hex: 'f59e0b', category: 'storage' },
  { title: 'Logs', icon: 'mdi:text-box-outline', hex: '64748b', category: 'monitoring' },
  { title: 'Metrics', icon: 'mdi:chart-line', hex: '22c55e', category: 'monitoring' },
  { title: 'Alert', icon: 'mdi:bell-outline', hex: 'ef4444', category: 'monitoring' },

  // === Homelab Services ===
  { title: 'Proxmox', icon: 'si:proxmox', hex: 'E57000', category: 'infrastructure' },
  { title: 'TrueNAS', icon: 'si:truenas', hex: '0095D5', category: 'storage' },
  { title: 'Portainer', icon: 'si:portainer', hex: '13BEF9', category: 'devops' },
  { title: 'Home Assistant', icon: 'si:homeassistant', hex: '41BDF5', category: 'brands' },
  { title: 'Pi-hole', icon: 'si:pihole', hex: '96060C', category: 'network' },
  { title: 'Plex', icon: 'si:plex', hex: 'EBAF00', category: 'brands' },
  { title: 'Jellyfin', icon: 'si:jellyfin', hex: '00A4DC', category: 'brands' },
  { title: 'Nextcloud', icon: 'si:nextcloud', hex: '0082C9', category: 'storage' },
  { title: 'Bitwarden', icon: 'si:bitwarden', hex: '175DDC', category: 'security' },
  { title: 'Wireguard', icon: 'si:wireguard', hex: '88171A', category: 'security' },
  { title: 'Tailscale', icon: 'si:tailscale', hex: '000000', category: 'network' },
  { title: 'Caddy', icon: 'si:caddy', hex: '1F88C0', category: 'infrastructure' },
  { title: 'MinIO', icon: 'si:minio', hex: 'C72E49', category: 'storage' },
  { title: 'Vault', icon: 'si:vault', hex: 'FFEC6E', category: 'security' },
  { title: 'Consul', icon: 'si:consul', hex: 'F24C53', category: 'infrastructure' },
  { title: 'Camunda', icon: 'si:camunda', hex: 'F5B700', category: 'devops' },

  // === Homelab Services (Dashboard Icons - Pre-colored) ===
  // Note: Dashboard icons are pre-colored, hex value is only for reference/fallback
  { title: 'Plex (Homelab)', icon: 'homelab:plex', hex: 'EBAF00', category: 'brands' },
  { title: 'Jellyfin (Homelab)', icon: 'homelab:jellyfin', hex: '00A4DC', category: 'brands' },
  { title: 'Emby', icon: 'homelab:emby', hex: '52B54B', category: 'brands' },
  { title: 'Sonarr', icon: 'homelab:sonarr', hex: '4051B5', category: 'brands' },
  { title: 'Radarr', icon: 'homelab:radarr', hex: 'FFC230', category: 'brands' },
  { title: 'Lidarr', icon: 'homelab:lidarr', hex: '159552', category: 'brands' },
  { title: 'Prowlarr', icon: 'homelab:prowlarr', hex: 'FF6C2C', category: 'brands' },
  { title: 'Overseerr', icon: 'homelab:overseerr', hex: '2D7CFF', category: 'brands' },
  { title: 'Tautulli', icon: 'homelab:tautulli', hex: 'F29F00', category: 'brands' },
  { title: 'Home Assistant (HL)', icon: 'homelab:home-assistant', hex: '41BDF5', category: 'brands' },
  { title: 'AdGuard Home', icon: 'homelab:adguard-home', hex: '68BC71', category: 'network' },
  { title: 'Pi-hole (Homelab)', icon: 'homelab:pi-hole', hex: '96060C', category: 'network' },
  { title: 'Proxmox (Homelab)', icon: 'homelab:proxmox', hex: 'E57000', category: 'infrastructure' },
  { title: 'Portainer (Homelab)', icon: 'homelab:portainer', hex: '13BEF9', category: 'devops' },
  { title: 'Unraid', icon: 'homelab:unraid', hex: 'F15A2C', category: 'infrastructure' },
  { title: 'TrueNAS (Homelab)', icon: 'homelab:truenas-scale', hex: '0095D5', category: 'storage' },
  { title: 'OpenMediaVault', icon: 'homelab:openmediavault', hex: '5DACDF', category: 'storage' },
  { title: 'Nextcloud (Homelab)', icon: 'homelab:nextcloud', hex: '0082C9', category: 'storage' },
  { title: 'Syncthing', icon: 'homelab:syncthing', hex: '0891D1', category: 'storage' },
  { title: 'Vaultwarden', icon: 'homelab:vaultwarden', hex: '175DDC', category: 'security' },
  { title: 'Gitea', icon: 'homelab:gitea', hex: '609926', category: 'devops' },
  { title: 'Forgejo', icon: 'homelab:forgejo', hex: 'FB923C', category: 'devops' },
  { title: 'Uptime Kuma', icon: 'homelab:uptime-kuma', hex: '5CDD8B', category: 'monitoring' },
  { title: 'Grafana (Homelab)', icon: 'homelab:grafana', hex: 'F46800', category: 'monitoring' },
  { title: 'Prometheus (Homelab)', icon: 'homelab:prometheus', hex: 'E6522C', category: 'monitoring' },
  { title: 'Traefik (Homelab)', icon: 'homelab:traefik', hex: '24A1C1', category: 'infrastructure' },
  { title: 'Nginx Proxy Manager', icon: 'homelab:nginx-proxy-manager', hex: 'F15833', category: 'infrastructure' },
  { title: 'Authentik', icon: 'homelab:authentik', hex: 'FD4B2D', category: 'security' },
  { title: 'Authelia', icon: 'homelab:authelia', hex: '0088CC', category: 'security' },
  { title: 'Kavita', icon: 'homelab:kavita', hex: '67B7DC', category: 'brands' },
  { title: 'Audiobookshelf', icon: 'homelab:audiobookshelf', hex: 'F59E0B', category: 'brands' },
  { title: 'Calibre', icon: 'homelab:calibre-web', hex: 'FF9800', category: 'brands' },
  { title: 'Paperless-ngx', icon: 'homelab:paperless-ngx', hex: '59C370', category: 'brands' },
  { title: 'Firefly III', icon: 'homelab:firefly-iii', hex: 'E8662F', category: 'brands' },
  { title: 'Immich', icon: 'homelab:immich', hex: '4250AF', category: 'brands' },
  { title: 'PhotoPrism', icon: 'homelab:photoprism', hex: '80CBC4', category: 'brands' },
  { title: 'Mealie', icon: 'homelab:mealie', hex: 'E58325', category: 'brands' },
  { title: 'Miniflux', icon: 'homelab:miniflux', hex: '4A90E2', category: 'brands' },
  { title: 'FreshRSS', icon: 'homelab:freshrss', hex: '5CB85C', category: 'brands' },
  { title: 'Ntfy', icon: 'homelab:ntfy', hex: '317B6E', category: 'brands' },
  { title: 'Actual Budget', icon: 'homelab:actual-budget', hex: '3D5EFF', category: 'brands' },
  { title: 'Tandoor', icon: 'homelab:tandoor-recipes', hex: 'F59E0B', category: 'brands' },
  { title: 'Kasm', icon: 'homelab:kasm', hex: '04B4E0', category: 'infrastructure' },
  { title: 'Guacamole', icon: 'homelab:guacamole', hex: '2A9342', category: 'infrastructure' },
  { title: 'WireGuard (Homelab)', icon: 'homelab:wireguard', hex: '88171A', category: 'security' },
  { title: 'OpenVPN', icon: 'homelab:openvpn', hex: 'EA7E20', category: 'security' },
  { title: 'UniFi', icon: 'homelab:unifi', hex: '0559C9', category: 'network' },
  { title: 'pfSense', icon: 'homelab:pfsense', hex: '212121', category: 'network' },
  { title: 'OPNsense', icon: 'homelab:opnsense', hex: 'D94F00', category: 'network' },
  { title: 'Komga', icon: 'homelab:komga', hex: '5E5E5E', category: 'brands' },
  { title: 'Beszel', icon: 'homelab:beszel', hex: '1E40AF', category: 'monitoring' },
  { title: 'Deluge', icon: 'homelab:deluge', hex: '0C4B33', category: 'brands' },
  { title: 'JellySeer', icon: 'homelab:jellyseerr', hex: '00A4DC', category: 'brands' },
  { title: 'Homepage', icon: 'mdi:view-dashboard-outline', hex: 'FF6B35', category: 'brands' },
  { title: 'DomainLocker', icon: 'mdi:dns-outline', hex: '6366F1', category: 'security' },
  { title: 'Dokploy', icon: 'homelab:dokploy', hex: '000000', category: 'devops' },

  // === Carbon Icons (Enterprise/Cloud) ===
  { title: 'Bare Metal', icon: 'carbon:bare-metal-server', hex: '64748b', category: 'infrastructure' },
  { title: 'VM Instance', icon: 'carbon:virtual-machine', hex: '3b82f6', category: 'infrastructure' },
  { title: 'Data Center', icon: 'carbon:data-center', hex: '64748b', category: 'infrastructure' },
  { title: 'Network', icon: 'carbon:network-3', hex: '3b82f6', category: 'network' },
  { title: 'Network 4', icon: 'carbon:network-4', hex: 'BF0B43', category: 'network' },
  { title: 'Data Center 1', icon: 'carbon:data-center', hex: '0F62FE', category: 'infrastructure' },
  { title: 'Security Shield', icon: 'carbon:security', hex: '22c55e', category: 'security' },
  { title: 'User', icon: 'carbon:user', hex: '64748b', category: 'brands' },
  { title: 'Users', icon: 'carbon:group', hex: '64748b', category: 'brands' },
  { title: 'Application', icon: 'carbon:application', hex: '3b82f6', category: 'infrastructure' },
  { title: 'Terminal', icon: 'carbon:terminal', hex: '64748b', category: 'infrastructure' },
  { title: 'Code', icon: 'carbon:code', hex: '64748b', category: 'brands' },

  // === Project Structure / Monorepo ===
  { title: 'Monorepo', icon: 'mdi:folder-multiple', hex: 'f59e0b', category: 'infrastructure' },
  { title: 'Monorepo Root', icon: 'mdi:folder-star', hex: 'f59e0b', category: 'infrastructure' },
  { title: 'Workspace', icon: 'mdi:folder-cog', hex: '8b5cf6', category: 'infrastructure' },
  { title: 'Package', icon: 'mdi:package-variant', hex: '22c55e', category: 'infrastructure' },
  { title: 'Utils Package', icon: 'mdi:package-variant-closed', hex: '06b6d4', category: 'infrastructure' },
  { title: 'Shared Package', icon: 'mdi:package-variant-closed-plus', hex: '8b5cf6', category: 'infrastructure' },
  { title: 'Turborepo', icon: 'si:turborepo', hex: 'EF4444', category: 'devops' },
  { title: 'Nx', icon: 'si:nx', hex: '143055', category: 'devops' },
  { title: 'Lerna', icon: 'si:lerna', hex: '9333EA', category: 'devops' },
  { title: 'pnpm', icon: 'si:pnpm', hex: 'F69220', category: 'devops' },
  { title: 'npm', icon: 'si:npm', hex: 'CB3837', category: 'devops' },
  { title: 'Yarn', icon: 'si:yarn', hex: '2C8EBB', category: 'devops' },
  { title: 'Bun', icon: 'si:bun', hex: 'FBF0DF', category: 'devops' },

  // === Application Types ===
  { title: 'Web App', icon: 'mdi:application', hex: '3b82f6', category: 'frontend' },
  { title: 'Mobile App', icon: 'mdi:cellphone', hex: '22c55e', category: 'frontend' },
  { title: 'Desktop App', icon: 'mdi:monitor', hex: '8b5cf6', category: 'frontend' },
  { title: 'Admin Panel', icon: 'mdi:view-dashboard', hex: 'f59e0b', category: 'frontend' },
  { title: 'Dashboard', icon: 'mdi:view-dashboard-variant', hex: 'f59e0b', category: 'frontend' },
  { title: 'Landing Page', icon: 'mdi:web', hex: '06b6d4', category: 'frontend' },
  { title: 'Marketing Site', icon: 'mdi:bullhorn', hex: 'ec4899', category: 'frontend' },
  { title: 'E-commerce', icon: 'mdi:cart', hex: '22c55e', category: 'frontend' },
  { title: 'Blog', icon: 'mdi:post', hex: 'f97316', category: 'frontend' },
  { title: 'Portal', icon: 'mdi:door-open', hex: '3b82f6', category: 'frontend' },
  { title: 'PWA', icon: 'mdi:cellphone-link', hex: '8b5cf6', category: 'frontend' },
  { title: 'SPA', icon: 'mdi:application-brackets', hex: '3b82f6', category: 'frontend' },
  { title: 'SSR App', icon: 'mdi:server', hex: '22c55e', category: 'frontend' },
  { title: 'Static Site', icon: 'mdi:file-document', hex: '64748b', category: 'frontend' },

  // === Backend Services ===
  { title: 'API Service', icon: 'mdi:api', hex: '22c55e', category: 'backend' },
  { title: 'REST API', icon: 'mdi:api', hex: '3b82f6', category: 'backend' },
  { title: 'Backend', icon: 'mdi:server', hex: '22c55e', category: 'backend' },
  { title: 'Auth Service', icon: 'mdi:shield-account', hex: 'ef4444', category: 'backend' },
  { title: 'Payment Service', icon: 'mdi:credit-card', hex: '22c55e', category: 'backend' },
  { title: 'Notification Service', icon: 'mdi:bell', hex: 'f59e0b', category: 'backend' },
  { title: 'Email Service', icon: 'mdi:email', hex: '3b82f6', category: 'backend' },
  { title: 'File Service', icon: 'mdi:file-cloud', hex: '8b5cf6', category: 'backend' },
  { title: 'Search Service', icon: 'mdi:magnify', hex: 'f59e0b', category: 'backend' },
  { title: 'Analytics Service', icon: 'mdi:chart-bar', hex: '06b6d4', category: 'backend' },
  { title: 'Scheduler', icon: 'mdi:calendar-clock', hex: '64748b', category: 'backend' },
  { title: 'Worker', icon: 'mdi:cog', hex: '64748b', category: 'backend' },
  { title: 'Background Job', icon: 'mdi:progress-clock', hex: 'f59e0b', category: 'backend' },
  { title: 'Gateway', icon: 'mdi:gate', hex: '8b5cf6', category: 'backend' },

  // === UI Libraries & Components ===
  { title: 'UI Library', icon: 'mdi:palette', hex: 'ec4899', category: 'frontend' },
  { title: 'Component Library', icon: 'mdi:puzzle', hex: '8b5cf6', category: 'frontend' },
  { title: 'Design System', icon: 'mdi:palette-swatch', hex: 'ec4899', category: 'frontend' },
  { title: 'Storybook', icon: 'si:storybook', hex: 'FF4785', category: 'frontend' },
  { title: 'Shadcn UI', icon: 'si:shadcnui', hex: '000000', category: 'frontend' },
  { title: 'Radix UI', icon: 'si:radixui', hex: '161618', category: 'frontend' },
  { title: 'Material UI', icon: 'si:mui', hex: '007FFF', category: 'frontend' },
  { title: 'Chakra UI', icon: 'si:chakraui', hex: '319795', category: 'frontend' },
  { title: 'Ant Design', icon: 'si:antdesign', hex: '0170FE', category: 'frontend' },
  { title: 'Bootstrap', icon: 'si:bootstrap', hex: '7952B3', category: 'frontend' },
  { title: 'Headless UI', icon: 'si:headlessui', hex: '66E3FF', category: 'frontend' },

  // === Documentation ===
  { title: 'Documentation', icon: 'mdi:book-open-page-variant', hex: '3b82f6', category: 'brands' },
  { title: 'Docs', icon: 'mdi:file-document-multiple', hex: '3b82f6', category: 'brands' },
  { title: 'API Docs', icon: 'mdi:file-code', hex: '22c55e', category: 'brands' },
  { title: 'Wiki', icon: 'mdi:wikipedia', hex: '64748b', category: 'brands' },
  { title: 'README', icon: 'mdi:file-document-outline', hex: '64748b', category: 'brands' },
  { title: 'Changelog', icon: 'mdi:history', hex: 'f59e0b', category: 'brands' },
  { title: 'Docusaurus', icon: 'si:docusaurus', hex: '3ECC5F', category: 'brands' },
  { title: 'GitBook', icon: 'si:gitbook', hex: '3884FF', category: 'brands' },
  { title: 'Notion', icon: 'si:notion', hex: '000000', category: 'brands' },
  { title: 'Confluence', icon: 'si:confluence', hex: '172B4D', category: 'brands' },

  // === Code Quality & Linting ===
  { title: 'ESLint', icon: 'si:eslint', hex: '4B32C3', category: 'devops' },
  { title: 'Prettier', icon: 'si:prettier', hex: 'F7B93E', category: 'devops' },
  { title: 'Stylelint', icon: 'si:stylelint', hex: '263238', category: 'devops' },
  { title: 'Husky', icon: 'mdi:dog', hex: '64748b', category: 'devops' },
  { title: 'Lint Staged', icon: 'mdi:check-circle', hex: '22c55e', category: 'devops' },
  { title: 'Commitlint', icon: 'si:commitlint', hex: '000000', category: 'devops' },
  { title: 'EditorConfig', icon: 'si:editorconfig', hex: 'FEFEFE', category: 'devops' },
  { title: 'SonarQube', icon: 'si:sonarqubeserver', hex: '4E9BCD', category: 'devops' },
  { title: 'CodeClimate', icon: 'si:codeclimate', hex: '000000', category: 'devops' },

  // === Testing ===
  { title: 'Jest', icon: 'si:jest', hex: 'C21325', category: 'devops' },
  { title: 'Vitest', icon: 'si:vitest', hex: '6E9F18', category: 'devops' },
  { title: 'Cypress', icon: 'si:cypress', hex: '17202C', category: 'devops' },
  { title: 'Playwright', icon: 'logos:playwright', hex: '2EAD33', category: 'devops' },
  { title: 'Testing Library', icon: 'si:testinglibrary', hex: 'E33332', category: 'devops' },
  { title: 'Mocha', icon: 'si:mocha', hex: '8D6748', category: 'devops' },
  { title: 'Selenium', icon: 'si:selenium', hex: '43B02A', category: 'devops' },
  { title: 'Puppeteer', icon: 'si:puppeteer', hex: '40B5A4', category: 'devops' },
  { title: 'Test Suite', icon: 'mdi:test-tube', hex: '22c55e', category: 'devops' },
  { title: 'Unit Tests', icon: 'mdi:test-tube', hex: '3b82f6', category: 'devops' },
  { title: 'E2E Tests', icon: 'mdi:test-tube-empty', hex: '8b5cf6', category: 'devops' },
  { title: 'Integration Tests', icon: 'mdi:test-tube-off', hex: 'f59e0b', category: 'devops' },

  // === Mobile Development ===
  { title: 'React Native', icon: 'si:react', hex: '61DAFB', category: 'frontend' },
  { title: 'Expo', icon: 'si:expo', hex: '000020', category: 'frontend' },
  { title: 'Flutter', icon: 'si:flutter', hex: '02569B', category: 'frontend' },
  { title: 'Dart', icon: 'si:dart', hex: '0175C2', category: 'frontend' },
  { title: 'Swift', icon: 'si:swift', hex: 'F05138', category: 'frontend' },
  { title: 'Kotlin', icon: 'si:kotlin', hex: '7F52FF', category: 'frontend' },
  { title: 'iOS App', icon: 'si:apple', hex: '000000', category: 'frontend' },
  { title: 'Android App', icon: 'si:android', hex: '3DDC84', category: 'frontend' },
  { title: 'Capacitor', icon: 'si:capacitor', hex: '119EFF', category: 'frontend' },
  { title: 'Ionic', icon: 'si:ionic', hex: '3880FF', category: 'frontend' },
  { title: 'Tauri', icon: 'si:tauri', hex: 'FFC131', category: 'frontend' },
  { title: 'Electron', icon: 'si:electron', hex: '47848F', category: 'frontend' },

  // === CMS & Content ===
  { title: 'Strapi', icon: 'si:strapi', hex: '4945FF', category: 'backend' },
  { title: 'Sanity', icon: 'si:sanity', hex: 'F03E2F', category: 'backend' },
  { title: 'Contentful', icon: 'si:contentful', hex: '2478CC', category: 'backend' },
  { title: 'WordPress', icon: 'si:wordpress', hex: '21759B', category: 'backend' },
  { title: 'Ghost', icon: 'si:ghost', hex: '15171A', category: 'backend' },
  { title: 'Directus', icon: 'si:directus', hex: '263238', category: 'backend' },
  { title: 'Payload CMS', icon: 'si:payloadcms', hex: '000000', category: 'backend' },
  { title: 'Keystonejs', icon: 'logos:keystonejs', hex: '166BFF', category: 'backend' },

  // === E-commerce Platforms ===
  { title: 'Shopify', icon: 'si:shopify', hex: '7AB55C', category: 'brands' },
  { title: 'WooCommerce', icon: 'si:woocommerce', hex: '96588A', category: 'brands' },
  { title: 'Medusa', icon: 'si:medusa', hex: '000000', category: 'backend' },
  { title: 'Saleor', icon: 'mdi:cart-outline', hex: '000000', category: 'backend' },

  // === Real-time & Communication ===
  { title: 'Socket.io', icon: 'si:socketdotio', hex: '010101', category: 'backend' },
  { title: 'WebSocket', icon: 'mdi:swap-horizontal', hex: '3b82f6', category: 'backend' },
  { title: 'WebRTC', icon: 'si:webrtc', hex: '333333', category: 'backend' },
  { title: 'Pusher', icon: 'si:pusher', hex: '300D4F', category: 'backend' },
  { title: 'Ably', icon: 'mdi:broadcast', hex: 'FF5416', category: 'backend' },
  { title: 'WebSocket Server', icon: 'mdi:lan-connect', hex: '010101', category: 'backend' },

  // === Search Engines ===
  { title: 'Algolia', icon: 'si:algolia', hex: '003DFF', category: 'backend' },
  { title: 'MeiliSearch', icon: 'si:meilisearch', hex: 'FF5CAA', category: 'backend' },
  { title: 'Typesense', icon: 'logos:typesense', hex: 'D52E7D', category: 'backend' },
  { title: 'OpenSearch', icon: 'si:opensearch', hex: '005EB8', category: 'backend' },

  // === Analytics & Tracking ===
  { title: 'Google Analytics', icon: 'si:googleanalytics', hex: 'E37400', category: 'monitoring' },
  { title: 'Mixpanel', icon: 'si:mixpanel', hex: '7856FF', category: 'monitoring' },
  { title: 'Amplitude', icon: 'logos:amplitude-icon', hex: '000000', category: 'monitoring' },
  { title: 'Segment', icon: 'logos:segment-icon', hex: '52BD95', category: 'monitoring' },
  { title: 'PostHog', icon: 'si:posthog', hex: 'F9BD2B', category: 'monitoring' },
  { title: 'Plausible', icon: 'si:plausibleanalytics', hex: '5850EC', category: 'monitoring' },
  { title: 'Umami', icon: 'si:umami', hex: '000000', category: 'monitoring' },
  { title: 'Hotjar', icon: 'si:hotjar', hex: 'FF3C00', category: 'monitoring' },
  { title: 'Google Tag Manager', icon: 'si:googletagmanager', hex: '246FDB', category: 'monitoring' },

  // === Feature Flags & A/B Testing ===
  { title: 'LaunchDarkly', icon: 'logos:launchdarkly-icon', hex: '3DD6F5', category: 'devops' },
  { title: 'Unleash', icon: 'mdi:toggle-switch-outline', hex: '1A4049', category: 'devops' },
  { title: 'Optimizely', icon: 'logos:optimizely', hex: '0037FF', category: 'devops' },
  { title: 'Feature Flag', icon: 'mdi:flag', hex: 'f59e0b', category: 'devops' },

  // === Error Tracking ===
  { title: 'Bugsnag', icon: 'logos:bugsnag', hex: '4949E4', category: 'monitoring' },
  { title: 'Rollbar', icon: 'si:rollbar', hex: '0A2540', category: 'monitoring' },
  { title: 'LogRocket', icon: 'mdi:record-circle-outline', hex: '764ABC', category: 'monitoring' },
  { title: 'Raygun', icon: 'mdi:bug-outline', hex: '6D2EAD', category: 'monitoring' },

  // === CI/CD & Build Tools ===
  { title: 'CircleCI', icon: 'si:circleci', hex: '343434', category: 'devops' },
  { title: 'Travis CI', icon: 'si:travisci', hex: '3EAAAF', category: 'devops' },
  { title: 'Drone', icon: 'si:drone', hex: '212121', category: 'devops' },
  { title: 'Buildkite', icon: 'si:buildkite', hex: '14CC80', category: 'devops' },
  { title: 'Webpack', icon: 'si:webpack', hex: '8DD6F9', category: 'devops' },
  { title: 'Vite', icon: 'si:vite', hex: '646CFF', category: 'devops' },
  { title: 'Rollup', icon: 'si:rollupdotjs', hex: 'EC4A3F', category: 'devops' },
  { title: 'esbuild', icon: 'si:esbuild', hex: 'FFCF00', category: 'devops' },
  { title: 'SWC', icon: 'si:swc', hex: 'FFFFFF', category: 'devops' },
  { title: 'Parcel', icon: 'mdi:package', hex: 'DFA440', category: 'devops' },
  { title: 'Gulp', icon: 'si:gulp', hex: 'CF4647', category: 'devops' },
  { title: 'Grunt', icon: 'si:grunt', hex: 'FAA918', category: 'devops' },
  { title: 'Make', icon: 'mdi:hammer-wrench', hex: '64748b', category: 'devops' },
  { title: 'Bazel', icon: 'si:bazel', hex: '43A047', category: 'devops' },

  // === Infrastructure as Code ===
  { title: 'Pulumi', icon: 'si:pulumi', hex: '8A3391', category: 'devops' },
  { title: 'CloudFormation', icon: 'mdi:cloud-braces', hex: 'FF9900', category: 'devops' },
  { title: 'CDK', icon: 'mdi:cloud-cog', hex: 'FF9900', category: 'devops' },
  { title: 'Crossplane', icon: 'logos:crossplane', hex: 'FFCD3C', category: 'devops' },

  // === Service Mesh & Networking ===
  { title: 'Istio', icon: 'si:istio', hex: '466BB0', category: 'infrastructure' },
  { title: 'Envoy', icon: 'si:envoyproxy', hex: 'AC6199', category: 'infrastructure' },
  { title: 'Linkerd', icon: 'si:linkerd', hex: '2BEDA7', category: 'infrastructure' },
  { title: 'Kong', icon: 'si:kong', hex: '003459', category: 'infrastructure' },
  { title: 'Jaeger', icon: 'si:jaeger', hex: '6BF178', category: 'monitoring' },

  // === Secrets Management ===
  { title: 'AWS Secrets Manager', icon: 'mdi:key-variant', hex: 'FF9900', category: 'security' },
  { title: 'Doppler', icon: 'homelab:doppler', hex: '000000', category: 'security' },
  { title: 'Infisical', icon: 'mdi:shield-key', hex: '22c55e', category: 'security' },
  { title: 'Secrets', icon: 'mdi:key', hex: 'f59e0b', category: 'security' },
  { title: 'Environment Variables', icon: 'mdi:variable', hex: '64748b', category: 'security' },

  // === Communication & Collaboration ===
  { title: 'Slack', icon: 'logos:slack-icon', hex: '4A154B', category: 'brands' },
  { title: 'Discord', icon: 'si:discord', hex: '5865F2', category: 'brands' },
  { title: 'Microsoft Teams', icon: 'logos:microsoft-teams', hex: '6264A7', category: 'brands' },
  { title: 'Zoom', icon: 'si:zoom', hex: '2D8CFF', category: 'brands' },

  // === Project Management ===
  { title: 'Jira', icon: 'si:jira', hex: '0052CC', category: 'brands' },
  { title: 'Linear', icon: 'si:linear', hex: '5E6AD2', category: 'brands' },
  { title: 'Asana', icon: 'si:asana', hex: 'F06A6A', category: 'brands' },
  { title: 'Trello', icon: 'si:trello', hex: '0052CC', category: 'brands' },
  { title: 'Monday', icon: 'logos:monday-icon', hex: 'FF3D57', category: 'brands' },
  { title: 'ClickUp', icon: 'si:clickup', hex: '7B68EE', category: 'brands' },

  // === More Languages ===
  { title: 'Ruby', icon: 'si:ruby', hex: 'CC342D', category: 'backend' },
  { title: 'Rails', icon: 'si:rubyonrails', hex: 'CC0000', category: 'backend' },
  { title: 'Elixir', icon: 'si:elixir', hex: '4B275F', category: 'backend' },
  { title: 'Phoenix', icon: 'si:phoenixframework', hex: 'FD4F00', category: 'backend' },
  { title: 'Scala', icon: 'si:scala', hex: 'DC322F', category: 'backend' },
  { title: 'Clojure', icon: 'si:clojure', hex: '5881D8', category: 'backend' },
  { title: 'Haskell', icon: 'si:haskell', hex: '5D4F85', category: 'backend' },
  { title: 'Erlang', icon: 'si:erlang', hex: 'A90533', category: 'backend' },
  { title: 'Lua', icon: 'si:lua', hex: '2C2D72', category: 'backend' },
  { title: 'Perl', icon: 'si:perl', hex: '39457E', category: 'backend' },
  { title: 'R', icon: 'si:r', hex: '276DC3', category: 'backend' },
  { title: 'Julia', icon: 'si:julia', hex: '9558B2', category: 'backend' },
  { title: 'Zig', icon: 'si:zig', hex: 'F7A41D', category: 'backend' },
  { title: 'C', icon: 'si:c', hex: 'A8B9CC', category: 'backend' },
  { title: 'C++', icon: 'si:cplusplus', hex: '00599C', category: 'backend' },
  { title: 'Assembly', icon: 'mdi:memory', hex: '64748b', category: 'backend' },
  { title: 'WebAssembly', icon: 'si:webassembly', hex: '654FF0', category: 'backend' },
  { title: 'Solidity', icon: 'si:solidity', hex: '363636', category: 'backend' },

  // === More Databases ===
  { title: 'MariaDB', icon: 'si:mariadb', hex: '003545', category: 'database' },
  { title: 'Oracle', icon: 'logos:oracle', hex: 'F80000', category: 'database' },
  { title: 'SQL Server', icon: 'homelab:microsoft-sql-server', hex: 'CC2927', category: 'database' },
  { title: 'CockroachDB', icon: 'si:cockroachlabs', hex: '6933FF', category: 'database' },
  { title: 'PlanetScale', icon: 'si:planetscale', hex: '000000', category: 'database' },
  { title: 'Neon', icon: 'si:neon', hex: '00E699', category: 'database' },
  { title: 'Turso', icon: 'si:turso', hex: '4FF8D2', category: 'database' },
  { title: 'DynamoDB', icon: 'logos:aws-dynamodb', hex: '4053D6', category: 'database' },
  { title: 'FaunaDB', icon: 'si:fauna', hex: '3A1AB6', category: 'database' },
  { title: 'Neo4j', icon: 'si:neo4j', hex: '4581C3', category: 'database' },
  { title: 'ArangoDB', icon: 'si:arangodb', hex: 'DDE072', category: 'database' },
  { title: 'Couchbase', icon: 'si:couchbase', hex: 'EA2328', category: 'database' },
  { title: 'CouchDB', icon: 'si:apachecouchdb', hex: 'E42528', category: 'database' },
  { title: 'RethinkDB', icon: 'logos:rethinkdb', hex: '28382E', category: 'database' },
  { title: 'Memcached', icon: 'mdi:memory', hex: '64748b', category: 'database' },
  { title: 'Dragonfly', icon: 'mdi:butterfly', hex: 'ef4444', category: 'database' },
  { title: 'Valkey', icon: 'mdi:key-chain', hex: 'DC382D', category: 'database' },
  { title: 'KeyDB', icon: 'mdi:key-chain-variant', hex: 'DC382D', category: 'database' },
  { title: 'TimescaleDB', icon: 'si:timescale', hex: 'FDB515', category: 'database' },
  { title: 'QuestDB', icon: 'homelab:questdb', hex: 'D14671', category: 'database' },
  { title: 'ClickHouse', icon: 'si:clickhouse', hex: 'FFCC01', category: 'database' },
  { title: 'Snowflake', icon: 'si:snowflake', hex: '29B5E8', category: 'database' },
  { title: 'BigQuery', icon: 'si:googlebigquery', hex: '669DF6', category: 'database' },
  { title: 'Redshift', icon: 'logos:aws-redshift', hex: '8C4FFF', category: 'database' },
  { title: 'Databricks', icon: 'si:databricks', hex: 'FF3621', category: 'database' },

  // === More Cloud Services ===
  { title: 'Linode', icon: 'logos:linode', hex: '00A95C', category: 'cloud' },
  { title: 'Vultr', icon: 'si:vultr', hex: '007BFC', category: 'cloud' },
  { title: 'Hetzner', icon: 'si:hetzner', hex: 'D50C2D', category: 'cloud' },
  { title: 'OVH', icon: 'si:ovh', hex: '123F6D', category: 'cloud' },
  { title: 'Scaleway', icon: 'si:scaleway', hex: '4F0599', category: 'cloud' },
  { title: 'Render', icon: 'si:render', hex: '46E3B7', category: 'cloud' },
  { title: 'Railway', icon: 'si:railway', hex: '0B0D0E', category: 'cloud' },
  { title: 'Fly.io', icon: 'si:flydotio', hex: '7B3BE2', category: 'cloud' },
  { title: 'Deno Deploy', icon: 'si:deno', hex: '000000', category: 'cloud' },
  { title: 'Cloudflare Workers', icon: 'si:cloudflareworkers', hex: 'F38020', category: 'cloud' },
  { title: 'Cloudflare Pages', icon: 'si:cloudflarepages', hex: 'F38020', category: 'cloud' },
  { title: 'Cloudflare R2', icon: 'si:cloudflare', hex: 'F38020', category: 'cloud' },
  { title: 'Cloudflare Tunnel', icon: 'si:cloudflare', hex: 'F38020', category: 'cloud' },
  { title: 'AWS Lambda', icon: 'logos:aws-lambda', hex: 'FF9900', category: 'cloud' },
  { title: 'Azure Functions', icon: 'mdi:function', hex: '0078D4', category: 'cloud' },
  { title: 'Google Cloud Functions', icon: 'logos:google-cloud-functions', hex: '4285F4', category: 'cloud' },

  // === Generic Architecture Components ===
  { title: 'Frontend', icon: 'mdi:monitor', hex: '3b82f6', category: 'frontend' },
  { title: 'Client', icon: 'mdi:laptop', hex: '3b82f6', category: 'frontend' },
  { title: 'Browser', icon: 'mdi:web', hex: '3b82f6', category: 'frontend' },
  { title: 'Service', icon: 'mdi:cog', hex: '22c55e', category: 'backend' },
  { title: 'Module', icon: 'mdi:puzzle', hex: '8b5cf6', category: 'infrastructure' },
  { title: 'Library', icon: 'mdi:library', hex: 'f59e0b', category: 'infrastructure' },
  { title: 'SDK', icon: 'mdi:package-variant', hex: '06b6d4', category: 'infrastructure' },
  { title: 'Plugin', icon: 'mdi:power-plug', hex: '22c55e', category: 'infrastructure' },
  { title: 'Extension', icon: 'mdi:puzzle-plus', hex: '8b5cf6', category: 'infrastructure' },
  { title: 'Adapter', icon: 'mdi:connection', hex: 'f59e0b', category: 'infrastructure' },
  { title: 'Connector', icon: 'mdi:transit-connection-variant', hex: '3b82f6', category: 'infrastructure' },
  { title: 'Integration', icon: 'mdi:link-variant', hex: '22c55e', category: 'infrastructure' },
  { title: 'Middleware', icon: 'mdi:middleware', hex: '8b5cf6', category: 'infrastructure' },
  { title: 'Proxy', icon: 'mdi:arrow-decision', hex: 'f59e0b', category: 'infrastructure' },
  { title: 'Reverse Proxy', icon: 'mdi:arrow-decision-outline', hex: 'f59e0b', category: 'infrastructure' },
  { title: 'Sidecar', icon: 'mdi:motorbike', hex: '64748b', category: 'infrastructure' },
  { title: 'Agent', icon: 'mdi:robot', hex: '8b5cf6', category: 'infrastructure' },
  { title: 'Daemon', icon: 'mdi:ghost', hex: '64748b', category: 'infrastructure' },
  { title: 'Process', icon: 'mdi:cog-outline', hex: '64748b', category: 'infrastructure' },
  { title: 'Thread', icon: 'mdi:ray-start-arrow', hex: '64748b', category: 'infrastructure' },
  { title: 'Pool', icon: 'mdi:pool', hex: '06b6d4', category: 'infrastructure' },
  { title: 'Batch', icon: 'mdi:tray-arrow-down', hex: 'f59e0b', category: 'infrastructure' },
  { title: 'Stream', icon: 'mdi:waves', hex: '06b6d4', category: 'infrastructure' },
  { title: 'Pipeline', icon: 'mdi:pipe', hex: '8b5cf6', category: 'infrastructure' },
  { title: 'ETL', icon: 'mdi:transfer', hex: 'f59e0b', category: 'infrastructure' },
  { title: 'Data Lake', icon: 'mdi:waves-arrow-right', hex: '06b6d4', category: 'storage' },
  { title: 'Data Warehouse', icon: 'mdi:warehouse', hex: '8b5cf6', category: 'storage' },
  { title: 'Object Storage', icon: 'mdi:bucket', hex: 'f59e0b', category: 'storage' },
  { title: 'Block Storage', icon: 'mdi:cube', hex: '64748b', category: 'storage' },
  { title: 'File Storage', icon: 'mdi:folder', hex: 'f59e0b', category: 'storage' },
  { title: 'Service Worker', icon: 'mdi:worker', hex: 'FF6D00', category: 'infrastructure' },
  { title: 'HTTP Cache', icon: 'mdi:cached', hex: '795548', category: 'infrastructure' },
  { title: 'File Code', icon: 'mdi:file-code', hex: '4CAF50', category: 'infrastructure' },
  { title: 'Bell Ring', icon: 'mdi:bell-ring', hex: 'E91E63', category: 'infrastructure' },
  { title: 'Background Sync', icon: 'mdi:sync', hex: '2196F3', category: 'infrastructure' },
  { title: 'Web Share', icon: 'mdi:share-variant', hex: '607D8B', category: 'infrastructure' },
  { title: 'Database Sync', icon: 'mdi:database-sync', hex: '7B1FA2', category: 'database' },
  { title: 'Currency USD', icon: 'mdi:currency-usd', hex: '4CAF50', category: 'brands' },
  { title: 'Shield Check', icon: 'mdi:shield-check', hex: '9C27B0', category: 'security' },
  { title: 'Microphone', icon: 'mdi:microphone', hex: 'FF5722', category: 'infrastructure' },

  // === AI/ML Extended ===
  { title: 'LangChain', icon: 'si:langchain', hex: '1C3C3C', category: 'backend' },
  { title: 'LlamaIndex', icon: 'mdi:text-search', hex: '8b5cf6', category: 'backend' },
  { title: 'Anthropic', icon: 'si:anthropic', hex: 'D4A27F', category: 'brands' },
  { title: 'Cohere', icon: 'svgl:cohere', hex: '39594D', category: 'brands' },
  { title: 'Replicate', icon: 'si:replicate', hex: '000000', category: 'brands' },
  { title: 'Pinecone', icon: 'logos:pinecone', hex: '000000', category: 'database' },
  { title: 'Weaviate', icon: 'mdi:cube-outline', hex: '00D6A3', category: 'database' },
  { title: 'Chroma', icon: 'mdi:palette', hex: 'FFD700', category: 'database' },
  { title: 'Qdrant', icon: 'mdi:vector-point', hex: 'DC244C', category: 'database' },
  { title: 'Milvus', icon: 'mdi:database-search', hex: '00A1EA', category: 'database' },
  { title: 'Vector DB', icon: 'mdi:vector-triangle', hex: '8b5cf6', category: 'database' },
  { title: 'Embeddings', icon: 'mdi:vector-arrange-below', hex: '22c55e', category: 'backend' },
  { title: 'RAG', icon: 'mdi:file-search', hex: 'f59e0b', category: 'backend' },
  { title: 'Fine-tuning', icon: 'mdi:tune', hex: '8b5cf6', category: 'backend' },
  { title: 'Model', icon: 'mdi:brain', hex: 'ec4899', category: 'backend' },
  { title: 'LLM', icon: 'mdi:head-cog', hex: '8b5cf6', category: 'backend' },
  { title: 'GPT', icon: 'homelab:openai', hex: '412991', category: 'brands' },
  { title: 'Claude', icon: 'svgl:claude-ai-icon', hex: 'D97757', category: 'brands' },
  { title: 'Gemini', icon: 'si:googlegemini', hex: '8E75B2', category: 'brands' },
  { title: 'Ollama', icon: 'si:ollama', hex: '000000', category: 'brands' },
  { title: 'MLflow', icon: 'si:mlflow', hex: '0194E2', category: 'devops' },
  { title: 'Weights & Biases', icon: 'si:weightsandbiases', hex: 'FFBE00', category: 'devops' },
  { title: 'Jupyter', icon: 'si:jupyter', hex: 'F37626', category: 'brands' },
  { title: 'Notebook', icon: 'mdi:notebook', hex: 'f59e0b', category: 'brands' },
  { title: 'Colab', icon: 'si:googlecolab', hex: 'F9AB00', category: 'brands' },
  { title: 'Kaggle', icon: 'si:kaggle', hex: '20BEFF', category: 'brands' },
  { title: 'scikit-learn', icon: 'si:scikitlearn', hex: 'F7931E', category: 'backend' },
  { title: 'Pandas', icon: 'si:pandas', hex: '150458', category: 'backend' },
  { title: 'NumPy', icon: 'si:numpy', hex: '013243', category: 'backend' },
  { title: 'Keras', icon: 'si:keras', hex: 'D00000', category: 'backend' },
  { title: 'ONNX', icon: 'si:onnx', hex: '005CED', category: 'backend' },

  // === AI Providers & Models ===
  { title: 'Mistral AI', icon: 'homelab:mistral-ai', hex: 'FA520F', category: 'brands' },
  { title: 'Perplexity', icon: 'homelab:perplexity', hex: '1FB8CD', category: 'brands' },
  { title: 'DeepSeek', icon: 'homelab:deepseek', hex: '4D6BFE', category: 'brands' },
  { title: 'Grok', icon: 'homelab:grok', hex: '000000', category: 'brands' },
  { title: 'Meta Llama', icon: 'si:meta', hex: '0866FF', category: 'brands' },
  { title: 'ElevenLabs', icon: 'si:elevenlabs', hex: '000000', category: 'brands' },
  { title: 'Groq', icon: 'svgl:groq', hex: 'F55036', category: 'brands' },
  { title: 'Together AI', icon: 'svgl:togetherai_light', hex: '0F6FFF', category: 'brands' },
  { title: 'Stability AI', icon: 'svgl:stability-ai', hex: '9D39FF', category: 'brands' },

  // === AI Gateways & Local Runtimes ===
  { title: 'OpenRouter', icon: 'si:openrouter', hex: '6566F1', category: 'backend' },
  { title: 'LM Studio', icon: 'si:lmstudio', hex: '000000', category: 'backend' },
  { title: 'vLLM', icon: 'homelab:vllm', hex: '30A2FF', category: 'backend' },
  { title: 'Jan', icon: 'homelab:jan', hex: '4F46E5', category: 'backend' },
  { title: 'LibreChat', icon: 'homelab:librechat', hex: '2B2D31', category: 'backend' },
  { title: 'Open WebUI', icon: 'homelab:open-webui', hex: '000000', category: 'backend' },

  // === AI Coding & Agentic Dev Tools ===
  { title: 'Claude Code', icon: 'svgl:claude-ai-icon', hex: 'D97757', category: 'brands' },
  { title: 'GitHub Copilot', icon: 'homelab:github-copilot', hex: '000000', category: 'brands' },
  { title: 'OpenAI Codex', icon: 'homelab:openai', hex: '000000', category: 'brands' },
  { title: 'Windsurf', icon: 'si:windsurf', hex: '09B6A2', category: 'brands' },
  { title: 'Cline', icon: 'si:cline', hex: '000000', category: 'brands' },
  { title: 'Cody', icon: 'homelab:sourcegraph', hex: '00CBEC', category: 'brands' },
  { title: 'Aider', icon: 'mdi:console', hex: '14B8A6', category: 'brands' },
  { title: 'Bolt.new', icon: 'si:stackblitz', hex: '1389FD', category: 'brands' },
  { title: 'v0', icon: 'si:vercel', hex: '000000', category: 'brands' },
  { title: 'Replit Agent', icon: 'si:replit', hex: 'F26207', category: 'brands' },
  { title: 'Warp', icon: 'si:warp', hex: '01A4FF', category: 'brands' },

  // === Agent Frameworks & Orchestration ===
  { title: 'CrewAI', icon: 'si:crewai', hex: 'FF5A50', category: 'backend' },
  { title: 'LangGraph', icon: 'si:langgraph', hex: '1C3C3C', category: 'backend' },
  { title: 'Dify', icon: 'si:dify', hex: '1C64F2', category: 'backend' },
  { title: 'Flowise', icon: 'homelab:flowise', hex: '3B82F6', category: 'backend' },
  { title: 'n8n', icon: 'si:n8n', hex: 'EA4B71', category: 'devops' },
  { title: 'MCP', icon: 'si:modelcontextprotocol', hex: '000000', category: 'backend' },
  { title: 'AI Agent', icon: 'mdi:robot', hex: '8B5CF6', category: 'backend' },
  { title: 'Autonomous Agent', icon: 'mdi:robot-outline', hex: '7C3AED', category: 'backend' },
  { title: 'Multi-Agent System', icon: 'mdi:robot-industrial', hex: '6D28D9', category: 'backend' },

  // === Blockchain & Web3 ===
  { title: 'Ethereum', icon: 'si:ethereum', hex: '3C3C3D', category: 'brands' },
  { title: 'Bitcoin', icon: 'si:bitcoin', hex: 'F7931A', category: 'brands' },
  { title: 'Polygon', icon: 'si:polygon', hex: '7B3FE4', category: 'brands' },
  { title: 'Solana', icon: 'si:solana', hex: '9945FF', category: 'brands' },
  { title: 'Hardhat', icon: 'logos:hardhat', hex: 'FFF100', category: 'devops' },
  { title: 'Foundry', icon: 'mdi:anvil', hex: '64748b', category: 'devops' },
  { title: 'IPFS', icon: 'si:ipfs', hex: '65C2CB', category: 'infrastructure' },
  { title: 'The Graph', icon: 'logos:graphql', hex: '6747ED', category: 'infrastructure' },
  { title: 'Chainlink', icon: 'si:chainlink', hex: '375BD2', category: 'infrastructure' },
  { title: 'MetaMask', icon: 'logos:metamask-icon', hex: 'F6851B', category: 'security' },
  { title: 'OpenZeppelin', icon: 'si:openzeppelin', hex: '4E5EAA', category: 'security' },
  { title: 'Uniswap', icon: 'mdi:swap-horizontal', hex: 'FF007A', category: 'brands' },
  { title: 'Etherscan', icon: 'mdi:database-search', hex: '2860E1', category: 'brands' },

  // === Gaming & Graphics ===
  { title: 'Unity', icon: 'si:unity', hex: '000000', category: 'brands' },
  { title: 'Unreal Engine', icon: 'si:unrealengine', hex: '0E1128', category: 'brands' },
  { title: 'Godot', icon: 'si:godotengine', hex: '478CBF', category: 'brands' },
  { title: 'Three.js', icon: 'si:threedotjs', hex: '000000', category: 'frontend' },
  { title: 'WebGL', icon: 'si:webgl', hex: '990000', category: 'frontend' },
  { title: 'Blender', icon: 'si:blender', hex: 'E87D0D', category: 'brands' },
  { title: 'Nakama', icon: 'mdi:server-network', hex: 'FF6C37', category: 'backend' },

  // === IoT & Embedded ===
  { title: 'Raspberry Pi', icon: 'si:raspberrypi', hex: 'A22846', category: 'infrastructure' },
  { title: 'Arduino', icon: 'si:arduino', hex: '00979D', category: 'infrastructure' },
  { title: 'ESP32', icon: 'si:esphome', hex: '000000', category: 'infrastructure' },
  { title: 'MQTT', icon: 'si:mqtt', hex: '660066', category: 'infrastructure' },
  { title: 'Zigbee', icon: 'si:zigbee', hex: 'EB0443', category: 'infrastructure' },
  { title: 'Z-Wave', icon: 'logos:zwave', hex: '1B365D', category: 'infrastructure' },
  { title: 'Matter', icon: 'mdi:home-automation', hex: '000000', category: 'infrastructure' },
  { title: 'IoT Hub', icon: 'mdi:access-point', hex: '3b82f6', category: 'infrastructure' },
  { title: 'Edge Device', icon: 'mdi:chip', hex: '64748b', category: 'infrastructure' },
  { title: 'Sensor', icon: 'mdi:thermometer', hex: '22c55e', category: 'infrastructure' },
  { title: 'Actuator', icon: 'mdi:cog-transfer', hex: 'f59e0b', category: 'infrastructure' },
  { title: 'Node-RED', icon: 'si:nodered', hex: '8F0000', category: 'infrastructure' },
  { title: 'Smart Lights', icon: 'mdi:lightbulb-group', hex: 'FFC107', category: 'infrastructure' },
  { title: 'Motion Sensor', icon: 'mdi:motion-sensor', hex: '4CAF50', category: 'infrastructure' },
  { title: 'Smart Switch', icon: 'mdi:toggle-switch', hex: '9E9E9E', category: 'infrastructure' },
  { title: 'Google Assistant', icon: 'mdi:google-assistant', hex: '4285F4', category: 'brands' },
  { title: 'Security Camera', icon: 'mdi:cctv', hex: 'F44336', category: 'security' },

  // === Misc Tools & Services ===
  { title: 'Postman', icon: 'si:postman', hex: 'FF6C37', category: 'devops' },
  { title: 'Insomnia', icon: 'si:insomnia', hex: '4000BF', category: 'devops' },
  { title: 'Bruno', icon: 'si:bruno', hex: 'F4AA41', category: 'devops' },
  { title: 'Swagger', icon: 'si:swagger', hex: '85EA2D', category: 'devops' },
  { title: 'OpenAPI', icon: 'si:openapiinitiative', hex: '6BA539', category: 'devops' },
  { title: 'AsyncAPI', icon: 'mdi:api', hex: '009688', category: 'devops' },
  { title: 'Ngrok', icon: 'si:ngrok', hex: '1F1E37', category: 'devops' },
  { title: 'LocalTunnel', icon: 'mdi:tunnel', hex: '64748b', category: 'devops' },
  { title: 'VS Code', icon: 'logos:visual-studio-code', hex: '007ACC', category: 'brands' },
  { title: 'Cursor', icon: 'si:cursor', hex: '000000', category: 'brands' },
  { title: 'Neovim', icon: 'si:neovim', hex: '57A143', category: 'brands' },
  { title: 'JetBrains', icon: 'si:jetbrains', hex: '000000', category: 'brands' },
  { title: 'Zed', icon: 'si:zedindustries', hex: '084CCF', category: 'brands' },

  // === P3 additions (2026-07) ===
  // Auth & Identity
  { title: 'Clerk', icon: 'si:clerk', hex: '6C47FF', category: 'security' },
  { title: 'Okta', icon: 'si:okta', hex: '007DC1', category: 'security' },
  { title: 'Keycloak', icon: 'si:keycloak', hex: '4D4D4D', category: 'security' },
  { title: 'FusionAuth', icon: 'si:fusionauth', hex: 'F58320', category: 'security' },
  { title: 'WorkOS', icon: 'logos:workos', hex: '6363F1', category: 'security' },
  { title: 'Ory', icon: 'si:ory', hex: '4F46E5', category: 'security' },

  // Backend Frameworks
  { title: 'NestJS', icon: 'si:nestjs', hex: 'E0234E', category: 'backend' },
  { title: 'Fastify', icon: 'si:fastify', hex: '000000', category: 'backend' },
  { title: 'Hono', icon: 'si:hono', hex: 'E36002', category: 'backend' },
  { title: 'Koa', icon: 'si:koa', hex: '33333D', category: 'backend' },
  { title: 'Symfony', icon: 'si:symfony', hex: '000000', category: 'backend' },
  { title: 'Quarkus', icon: 'si:quarkus', hex: '4695EB', category: 'backend' },
  { title: 'AdonisJS', icon: 'si:adonisjs', hex: '5A45FF', category: 'backend' },

  // Frontend Frameworks & Libraries
  { title: 'Remix', icon: 'si:remix', hex: '000000', category: 'frontend' },
  { title: 'SolidJS', icon: 'si:solid', hex: '2C4F7C', category: 'frontend' },
  { title: 'Qwik', icon: 'si:qwik', hex: 'AC7EF4', category: 'frontend' },
  { title: 'Preact', icon: 'si:preact', hex: '673AB8', category: 'frontend' },
  { title: 'Gatsby', icon: 'si:gatsby', hex: '663399', category: 'frontend' },
  { title: 'HTMX', icon: 'si:htmx', hex: '3366CC', category: 'frontend' },
  { title: 'Alpine.js', icon: 'si:alpinedotjs', hex: '8BC0D0', category: 'frontend' },
  { title: 'Ember', icon: 'si:emberdotjs', hex: 'E04E39', category: 'frontend' },

  // Observability & Monitoring
  { title: 'OpenTelemetry', icon: 'si:opentelemetry', hex: '000000', category: 'monitoring' },
  { title: 'Splunk', icon: 'si:splunk', hex: '000000', category: 'monitoring' },
  { title: 'Dynatrace', icon: 'si:dynatrace', hex: '1496FF', category: 'monitoring' },
  { title: 'Kibana', icon: 'si:kibana', hex: '005571', category: 'monitoring' },
  { title: 'Logstash', icon: 'si:logstash', hex: '005571', category: 'monitoring' },

  // Queues & Streaming
  { title: 'NATS', icon: 'si:natsdotio', hex: '27AAE1', category: 'infrastructure' },
  { title: 'Apache Pulsar', icon: 'si:apachepulsar', hex: '188FFF', category: 'infrastructure' },
  { title: 'Celery', icon: 'si:celery', hex: '37814A', category: 'infrastructure' },

  // Cloud Services
  { title: 'AWS EC2', icon: 'logos:aws-ec2', hex: 'FF9900', category: 'cloud' },
  { title: 'AWS RDS', icon: 'logos:aws-rds', hex: '527FFF', category: 'cloud' },
  { title: 'AWS CloudFront', icon: 'logos:aws-cloudfront', hex: '8C4FFF', category: 'cloud' },
  { title: 'AWS ECS', icon: 'logos:aws-ecs', hex: 'FF9900', category: 'cloud' },
  { title: 'AWS EKS', icon: 'logos:aws-eks', hex: 'FF9900', category: 'cloud' },
  { title: 'Google Cloud Run', icon: 'logos:google-cloud-run', hex: '4285F4', category: 'cloud' },

  // DevOps
  { title: 'Helm', icon: 'si:helm', hex: '0F1689', category: 'devops' },
  { title: 'Podman', icon: 'si:podman', hex: '892CA0', category: 'devops' },
  { title: 'Vagrant', icon: 'si:vagrant', hex: '1868F2', category: 'devops' },
  { title: 'Packer', icon: 'si:packer', hex: '02A8EF', category: 'devops' },
  { title: 'Rancher', icon: 'si:rancher', hex: '0075A8', category: 'devops' },
  { title: 'Flux', icon: 'si:flux', hex: '5468FF', category: 'devops' },

  // Payments
  { title: 'PayPal', icon: 'si:paypal', hex: '002991', category: 'brands' },
  { title: 'Paddle', icon: 'si:paddle', hex: 'FDDD35', category: 'brands' },
  { title: 'Lemon Squeezy', icon: 'si:lemonsqueezy', hex: 'FFC233', category: 'brands' },
  { title: 'Adyen', icon: 'si:adyen', hex: '0ABF53', category: 'brands' },
  { title: 'Razorpay', icon: 'si:razorpay', hex: '0C2451', category: 'brands' },

  // CDN & Network
  { title: 'Fastly', icon: 'si:fastly', hex: 'FF282D', category: 'network' },
  { title: 'Akamai', icon: 'si:akamai', hex: '0096D6', category: 'network' },

  // Email & Messaging
  { title: 'Resend', icon: 'si:resend', hex: '000000', category: 'brands' },
  { title: 'Mailchimp', icon: 'si:mailchimp', hex: 'FFE01B', category: 'brands' },
  { title: 'SendGrid', icon: 'logos:sendgrid', hex: '1A82E2', category: 'brands' },
];

export const NODE_TYPES = {
  tech: 'techNode',
};

